app.service("ElevenLabsAPI", [
	"$http",
	"ENV",
	"Session",
	function ($http, ENV, Session) {
		const baseUri = ENV.API + "/api/elevenlabs-conversations";

		/**
		 * Generates the common headers required for API authentication.
		 * @returns {Object} The headers object.
		 */
		const getHeaders = function () {
			return {
				Authorization: "Bearer " + Session.token,
				"X-PRACTICE-ID": `${Session.practice.id}`,
			};
		};

		var service = {
			isProcessing: false,

			// =================================================================
			// Monitoring
			// =================================================================

			/**
			 * Get the monitoring statistics for a practice.
			 */
			getMonitoringStatistics: function (practiceId, startDateUtc, endDateUtc) {
				return $http({
					method: "GET",
					url: `${baseUri}/monitoring?practiceId=${encodeURIComponent(practiceId)}&startDateUtc=${encodeURIComponent(startDateUtc)}&endDateUtc=${encodeURIComponent(endDateUtc)}`,
					headers: { Authorization: "Bearer " + Session.token },
				});
			},

			// =================================================================
			// Voices
			// =================================================================

			/**
			 * List all the CatTrax voices.
			 */
			getVoices: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/voices`,
					headers: getHeaders(),
				});
			},

			// =================================================================
			// Phone Numbers
			// =================================================================

			/**
			 * List all the CatTrax available phone numbers.
			 */
			getPhoneNumbers: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/phone-numbers`,
					headers: getHeaders(),
				});
			},

			/**
			 * Import new phone number.
			 */
			importPhoneNumber: function (phoneNumber, practiceId) {
				return $http({
					method: "POST",
					url: `${baseUri}/phone-numbers?phoneNumber=${encodeURIComponent(phoneNumber)}`,
					headers: {
						Authorization: "Bearer " + Session.token,
						"X-PRACTICE-ID": `${practiceId}`,
					}
				});
			},

			// =================================================================
			// Agents
			// =================================================================

			/**
			 * List all the practice's agents.
			 */
			getAgents: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/agents`,
					headers: getHeaders(),
				});
			},

			/**
			 * Create a new agent.
			 * @param {Object} data - The agent's data.
			 */
			createAgent: function (data) {
				return $http({
					method: "POST",
					url: `${baseUri}/agents`,
					headers: getHeaders(),
					data: data,
				});
			},

			/**
			 * Get an agent by its ID.
			 * @param {string} agentId - The agent's unique identifier.
			 */
			getAgentById: function (agentId) {
				return $http({
					method: "GET",
					url: `${baseUri}/agents/${agentId}`,
					headers: getHeaders(),
				});
			},

			/**
			 * Update an existing agent.
			 * @param {string} agentId - The agent's unique identifier.
			 * @param {Object} data - The agent's updated data.
			 */
			updateAgent: function (agentId, data) {
				return $http({
					method: "PUT",
					url: `${baseUri}/agents/${agentId}`,
					headers: getHeaders(),
					data: data,
				});
			},

			/**
			 * Deletes an agent.
			 * @param {string} agentId - The agent's unique identifier.
			 */
			deleteAgent: function (agentId, deleteOrphans) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/agents/${agentId}?deleteOrphans=${deleteOrphans}`,
					headers: getHeaders(),
				});
			},

			// =================================================================
			// Prompts
			// =================================================================

			/**
			 * List all the practice's prompts, optionally filtered by agentId.
			 * @param {string} [agentId] - Optional agent ID to filter prompts.
			 */
			getPrompts: function (agentId) {
				return $http({
					method: "GET",
					url: `${baseUri}/prompts`,
					headers: getHeaders(),
					params: { agentId },
				});
			},

			/**
			 * Create a new prompt.
			 * @param {Object} data - The prompt's data.
			 */
			createPrompt: function (data) {
				return $http({
					method: "POST",
					url: `${baseUri}/prompts`,
					headers: getHeaders(),
					data: data,
				});
			},

			/**
			 * Get a prompt by its ID.
			 * @param {string} promptId - The prompt's unique identifier.
			 */
			getPromptById: function (promptId) {
				return $http({
					method: "GET",
					url: `${baseUri}/prompts/${promptId}`,
					headers: getHeaders(),
				});
			},

			/**
			 * Update an existing prompt.
			 * @param {string} promptId - The prompt's unique identifier.
			 * @param {Object} data - The prompt's updated data.
			 */
			updatePrompt: function (promptId, data) {
				return $http({
					method: "PUT",
					url: `${baseUri}/prompts/${promptId}`,
					headers: getHeaders(),
					data: data,
				});
			},

			/**
			 * Delete a prompt.
			 * @param {string} promptId - The prompt's unique identifier.
			 */
			deletePrompt: function (promptId) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/prompts/${promptId}`,
					headers: getHeaders(),
				});
			},

			// =================================================================
			// Documents
			// =================================================================

			/**
			 * List all the practice's documents.
			 */
			getDocuments: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/documents`,
					headers: getHeaders(),
				});
			},

			/**
			 * Uploads a new document.
			 * @param {FormData} formData - The FormData object containing the file and other details.
			 */
			createDocument: function (formData) {
				const headers = getHeaders();
				headers["Content-Type"] = undefined; // Let browser set Content-Type for multipart

				return $http({
					method: "POST",
					url: `${baseUri}/documents`,
					headers: headers,
					data: formData,
					transformRequest: angular.identity, // Prevent AngularJS from serializing data
				});
			},

			/**
			 * Updates a document.
			 * @param {FormData} formData - The FormData object containing the file and other details.
			 * @param {string} documentId - The document's unique identifier.
			 */
			updateDocument: function (documentId, formData) {
				const headers = getHeaders();
				headers["Content-Type"] = undefined; // Let browser set Content-Type for multipart

				return $http({
					method: "PUT",
					url: `${baseUri}/documents/${documentId}`,
					headers: headers,
					data: formData,
					transformRequest: angular.identity, // Prevent AngularJS from serializing data
				});
			},

			/**
			 * Delete a document.
			 * @param {string} documentId - The document's unique identifier.
			 */
			deleteDocument: function (documentId) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/documents/${documentId}`,
					headers: getHeaders(),
				});
			},

			// =================================================================
			// Calls
			// =================================================================

			/**
			 * List all the practice's outbound calls.
			 * @param {boolean} [isPending] - If true, gets calls that have not yet been successfully called.
			 */
			getOutboundCalls: function (isPending) {
				let config = {
					method: "GET",
					url: `${baseUri}/calls/outbound`,
					headers: getHeaders(),
				};
				if (isPending !== undefined && isPending !== null) {
					config.params = { isPending };
				}
				return $http(config);
			},

			/**
			 * List all the patient's outbound calls.
			 * @param {integer} [patientId] - The patient ID.
			 * @param {boolean} [isPending] - If true, gets calls that have not yet been successfully called.
			 */
			getOutboundCallsByPatientId: function (patientId, isPending) {
				let config = {
					method: "GET",
					url: `${baseUri}/calls/outbound/patient/` + patientId,
					headers: getHeaders(),
				};
				if (isPending !== undefined && isPending !== null) {
					config.params = { isPending };
				}
				return $http(config);
			},

			/**
			 * Schedule a new outbound call.
			 * @param {Object} data - The outbound call's data.
			 */
			createOutboundCall: function (data) {
				return $http({
					method: "POST",
					url: `${baseUri}/calls/outbound`,
					headers: getHeaders(),
					data: data,
				});
			},

			/**
			 * Get an outbound call by its ID.
			 * @param {string} outboundCallId - The outbound call's unique identifier.
			 */
			getOutboundCallById: function (outboundCallId) {
				return $http({
					method: "GET",
					url: `${baseUri}/calls/outbound/${outboundCallId}`,
					headers: getHeaders(),
				});
			},

			/**
			 * Update an outbound call.
			 * @param {string} outboundCallId - The outbound call's unique identifier.
			 * @param {Object} data - The outbound call's updated data.
			 */
			updateOutboundCall: function (outboundCallId, data) {
				return $http({
					method: "PUT",
					url: `${baseUri}/calls/outbound/${outboundCallId}`,
					headers: getHeaders(),
					data: data,
				});
			},

			/**
			 * Delete an outbound call.
			 * @param {string} outboundCallId - The outbound call's unique identifier.
			 */
			deleteOutboundCall: function (outboundCallId) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/calls/outbound/${outboundCallId}`,
					headers: getHeaders(),
				});
			},
		};

		return service;
	},
]);